import React from 'react';
import { DataGrid } from 'v3/Components/DataGrid/DataGrid';
import { BooleanSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/BooleanSelectFilter';
import { DateRangePickerFilterFilter } from 'v3/Components/DataGrid/ColumnFilters/DateRangePickerFilter';
import { MultiSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/MultiSelectFilter';
import { SingleSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/SingleSelectFilter';
import { TextFieldFilter } from 'v3/Components/DataGrid/ColumnFilters/TextFieldFilter';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import {
  buildEndpoint,
  defaultDataTransform,
  formatShortDate,
  formatShortDateTime,
  useDataFetcher,
} from 'Utilities';
import { useEnums, useGlobalContext } from 'Context';
import './Invoices.scss';

interface DropdownOptions {
  clients: { label: string; value: string }[];
  reviewers: { label: string; value: string }[];
  owners: { label: string; value: string }[];
  services: { label: string; value: string }[];
  subServices: { label: string; value: string }[];
  states: { label: string; value: string }[];
  specialties: { label: string; value: string }[];
  reviewerTypes: { label: string; value: string }[];
  lobs: { label: string; value: string }[];
  tats: { label: string; value: string }[];
  tatPriorities: { label: string; value: string }[];
  genders: { label: string; value: string }[];
  apContactPreferences: { label: string; value: string }[];
  contacts: { label: string; value: string }[];
  statuses: { label: string; value: string }[];
  outcomeOptions: { label: string; value: string }[];
}

const useInvoicesDropdownOptions = (): DropdownOptions => {
  const enums: any = useEnums();

  const [filterOptions] = useDataFetcher<{
    owners: { id: string; name: string }[];
    reviewers: { id: string; name: string }[];
    services: { id: string; name: string }[];
    subServices: { id: string; name: string }[];
  }>(
    buildEndpoint(ENDPOINTS.invoices.endpointString('FilterOptions')),
    { owners: [], reviewers: [], services: [], subServices: [] },
    defaultDataTransform,
    undefined,
    true,
    '3.0'
  );

  const [clientsList] = useDataFetcher<{ id: string; name: string }[]>(
    buildEndpoint(ENDPOINTS.clients.dropdownItems),
    [],
    defaultDataTransform
  );

  return {
    clients: (clientsList ?? [])
      .map((c) => ({ label: c.name, value: c.id }))
      .filter((c) => c.label && c.value),
    reviewers: (filterOptions?.reviewers ?? []).map((r) => ({
      label: r.name,
      value: r.id,
    })),
    owners: (filterOptions?.owners ?? []).map((o) => ({
      label: o.name,
      value: o.id,
    })),
    services: (filterOptions?.services ?? []).map((s) => ({
      label: s.name,
      value: s.id,
    })),
    subServices: (filterOptions?.subServices ?? []).map((s) => ({
      label: s.name,
      value: s.id,
    })),
    states: Object.values(enums?.state ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    specialties: Object.values(enums?.specialty ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    reviewerTypes: Object.values(enums?.reviewerType ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    lobs: Object.values(enums?.lineOfBusiness ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    tats: Object.values(enums?.tat ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    tatPriorities: Object.values(enums?.tatPriority ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    genders: Object.values(enums?.gender ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    apContactPreferences: Object.values(enums?.apContact ?? {}).map(
      (e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })
    ),
    contacts: Object.values(enums?.appointmentReportContact ?? {}).map(
      (e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })
    ),
    statuses: Object.values(enums?.appointmentStatus ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    outcomeOptions: Object.values(enums?.outcomeOfReview ?? {}).map(
      (e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })
    ),
  };
};

const buildInvoicesColumns = (opts: DropdownOptions): any[] => [
  // ── Default ON columns ─────────────────────────────────────────────
  {
    field: 'stradixNumber',
    headerName: 'Stradix #',
    width: 160,
    hideable: false,
    pinnable: false,
    cellClassName: 'invoices-cell-bold',
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'client',
    headerName: 'Client',
    width: 200,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.clients} />
    ),
  },
  {
    field: 'claimantFirstName',
    headerName: 'First Name',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantLastName',
    headerName: 'Last Name',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimNumber',
    headerName: 'Claim/Client #',
    width: 160,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'benefitState',
    headerName: 'State (Benefit)',
    width: 140,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.states} />
    ),
  },
  {
    field: 'service',
    headerName: 'Service',
    width: 200,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.services} />
    ),
  },
  {
    field: 'subService',
    headerName: 'Sub Service',
    width: 220,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.subServices} />
    ),
  },
  {
    field: 'tat',
    headerName: 'TAT',
    width: 140,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.tats} />
    ),
  },
  {
    field: 'referralDate',
    headerName: 'Referral Date',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'dueDateAndTime',
    headerName: 'Due Date',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'reportDateCompleted',
    headerName: 'Report Date Completed',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'closedDate',
    headerName: 'Case Closed Date',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'reviewedBy',
    headerName: 'Reviewer',
    width: 180,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.reviewers} />
    ),
  },
  {
    field: 'reviewerSpecialty',
    headerName: 'Reviewer Specialty',
    width: 200,

    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'referralSource',
    headerName: 'Referrer',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'lineOfBusiness',
    headerName: 'LOB',
    width: 260,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.lobs} />
    ),
  },
  {
    field: 'dateOfInjury',
    headerName: 'DOI/DOD',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDate(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} dateOnly />
    ),
  },
  {
    field: 'proxiedByUser',
    headerName: 'Proxied By',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'owner',
    headerName: 'Owner',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'intakeOwnerName',
    headerName: 'Intake Owner',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'assigningOwnerName',
    headerName: 'Assignment Owner',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'qaOwnerName',
    headerName: 'QA Owner',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'referralStatus',
    headerName: 'Status',
    width: 180,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.statuses} />
    ),
  },

  // ── Default OFF columns ────────────────────────────────────────────
  {
    field: 'reviewerType',
    headerName: 'Reviewer Type',
    width: 160,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.reviewerTypes} />
    ),
  },
  {
    field: 'reviewerPrimaryBoard',
    headerName: 'Primary Board',
    width: 200,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.specialties} />
    ),
  },
  {
    field: 'reviewerSubspecialty',
    headerName: 'Subspecialty',
    width: 240,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'callAttempts',
    headerName: 'Call Attempts',
    width: 140,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'referralNumber',
    headerName: 'Referral #',
    width: 160,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'reportDueDate',
    headerName: 'Report Due Date',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'reportDeterminationDate',
    headerName: 'Report Determination Date',
    width: 220,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'reportTitle',
    headerName: 'Report Title',
    width: 400,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'insuranceCarrier',
    headerName: 'Insurance Carrier',
    width: 200,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'sameDay',
    headerName: 'SameDay',
    width: 140,
    sortable: false,
    renderHeaderFilter: (params: any) => <BooleanSelectFilter {...params} />,
  },
  {
    field: 'rush',
    headerName: 'Rush',
    width: 140,
    valueFormatter: (value: boolean) => (value ? 'Yes' : 'No'),
    renderHeaderFilter: (params: any) => <BooleanSelectFilter {...params} />,
  },
  {
    field: 'numberOfRequests',
    headerName: 'Number of Requests',
    width: 180,
    renderHeaderFilter: (params: any) => (
      <TextFieldFilter {...params} inputType="number" />
    ),
  },
  {
    field: 'reasonsForRequest',
    headerName: 'Reasons for Requests',
    width: 400,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'outcomeOfReview',
    headerName: 'Outcome of Review',
    width: 260,
    sortable: false,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.outcomeOptions} />
    ),
  },
  {
    field: 'employerName',
    headerName: 'Employer Name',
    width: 200,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'contact',
    headerName: 'Contact',
    width: 160,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.contacts} />
    ),
  },
  {
    field: 'pageCount',
    headerName: 'Page Count',
    width: 140,
    renderHeaderFilter: (params: any) => (
      <TextFieldFilter {...params} inputType="number" />
    ),
  },
  {
    field: 'tatPriority',
    headerName: 'TAT Priority',
    width: 140,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.tatPriorities} />
    ),
  },
  {
    field: 'jurisditionalMatchRequired',
    headerName: 'JM Required',
    width: 140,
    renderHeaderFilter: (params: any) => <BooleanSelectFilter {...params} />,
  },
  {
    field: 'attorneyName',
    headerName: 'Attorney Name',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'numberOfReviews',
    headerName: '# of Reviews',
    width: 140,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter
        {...params}
        optionItems={[1, 2, 3, 4, 5].map((n) => ({
          label: String(n),
          value: String(n),
        }))}
      />
    ),
  },
  {
    field: 'dateOfRequest',
    headerName: 'Date of Request',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'dateOfExam',
    headerName: 'Date of Exam',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDate(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} dateOnly />
    ),
  },
  {
    field: 'dateAndTimeRequestReceived',
    headerName: "Date & Time Request Rec'd",
    width: 220,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'externalAdjuster',
    headerName: 'External Adjuster',
    width: 180,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantGender',
    headerName: 'Gender',
    width: 140,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.genders} />
    ),
  },
  {
    field: 'claimantEmail',
    headerName: 'Claimant Email',
    width: 200,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantDateOfBirth',
    headerName: 'DOB',
    width: 200,
    widthOnFilter: 260,
    valueFormatter: (value: string) => formatShortDate(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} dateOnly />
    ),
  },
  {
    field: 'claimantStreetAddress',
    headerName: 'Claimant Street Address',
    width: 400,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantCity',
    headerName: 'Claimant City',
    width: 260,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantState',
    headerName: 'Claimant State',
    width: 140,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.states} />
    ),
  },
  {
    field: 'claimantZipCode',
    headerName: 'Claimant Zip Code',
    width: 140,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'attendance',
    headerName: 'Attendance',
    width: 140,
    sortable: false,
    filterable: false,
  },
  {
    field: 'requestedReviewerSpecialty1',
    headerName: 'Req. Reviewer Specialty 1',
    width: 200,
    sortable: false,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.specialties} />
    ),
  },
  {
    field: 'requestedReviewerSpecialty2',
    headerName: 'Req. Reviewer Specialty 2',
    width: 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'requestedReviewerSpecialty3',
    headerName: 'Req. Reviewer Specialty 3',
    width: 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'requestedReviewerSpecialty4',
    headerName: 'Req. Reviewer Specialty 4',
    width: 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'requestedReviewerSpecialty5',
    headerName: 'Req. Reviewer Specialty 5',
    width: 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apContactPreference',
    headerName: 'AP Contact Pref.',
    width: 160,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.apContactPreferences} />
    ),
  },
  {
    field: 'apName1',
    headerName: 'AP Name 1',
    width: 200,
    sortable: false,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'apName2',
    headerName: 'AP Name 2',
    width: 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apName3',
    headerName: 'AP Name 3',
    width: 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apName4',
    headerName: 'AP Name 4',
    width: 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apName5',
    headerName: 'AP Name 5',
    width: 200,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apSpecialty1',
    headerName: 'AP Specialty 1',
    width: 180,
    sortable: false,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.specialties} />
    ),
  },
  {
    field: 'apSpecialty2',
    headerName: 'AP Specialty 2',
    width: 180,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apSpecialty3',
    headerName: 'AP Specialty 3',
    width: 180,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apSpecialty4',
    headerName: 'AP Specialty 4',
    width: 180,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apSpecialty5',
    headerName: 'AP Specialty 5',
    width: 180,
    sortable: false,
    filterable: false,
  },
];

const defaultHiddenColumns: Record<string, boolean> = {
  // Default OFF
  reviewerType: false,
  reviewerPrimaryBoard: false,
  reviewerSubspecialty: false,
  callAttempts: false,
  referralNumber: false,
  reportDueDate: false,
  reportDeterminationDate: false,
  reportTitle: false,
  insuranceCarrier: false,
  sameDay: false,
  rush: false,
  numberOfRequests: false,
  reasonsForRequest: false,
  outcomeOfReview: false,
  employerName: false,
  pageCount: false,
  contact: false,
  tatPriority: false,
  jurisditionalMatchRequired: false,
  attorneyName: false,
  numberOfReviews: false,
  dateOfRequest: false,
  dateOfExam: false,
  dateAndTimeRequestReceived: false,
  team: false,
  externalAdjuster: false,
  claimantGender: false,
  claimantEmail: false,
  claimantDateOfBirth: false,
  claimantStreetAddress: false,
  claimantCity: false,
  claimantState: false,
  claimantZipCode: false,
  attendance: false,
  requestedReviewerSpecialty1: false,
  requestedReviewerSpecialty2: false,
  requestedReviewerSpecialty3: false,
  requestedReviewerSpecialty4: false,
  requestedReviewerSpecialty5: false,
  apContactPreference: false,
  apName1: false,
  apName2: false,
  apName3: false,
  apName4: false,
  apName5: false,
  apSpecialty1: false,
  apSpecialty2: false,
  apSpecialty3: false,
  apSpecialty4: false,
  apSpecialty5: false,
};

export const InvoicesPage: React.FC = () => {
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isInternal = userdata?.isInternal ?? false;
  const readEndpoint = buildEndpoint(ENDPOINTS.invoices.read);
  const exportEndpoint = buildEndpoint(
    ENDPOINTS.invoices.endpointString('ExportToCsv')
  );
  const dropdownOptions = useInvoicesDropdownOptions();
  const columns = React.useMemo(
    () => buildInvoicesColumns(dropdownOptions),
    [dropdownOptions]
  );

  const legacyParams = React.useMemo(
    () => ({
      readEndpoint,
      apiVersion: '3.0',
    }),
    [readEndpoint]
  );

  return (
    <div className="invoices-v3">
      <div className="main-grid-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>Case Reporting</h1>
        </div>
      </div>
      <div className="invoices-content">
        <DataGrid
          title="Cases"
          gridId="invoices"
          legacyParams={legacyParams}
          columns={columns}
          columnVisibilityModel={defaultHiddenColumns}
          pinnedColumns={{ left: ['stradixNumber'] }}
          exportEndpoint={exportEndpoint}
          exportFileName="Invoices"
          defaultShowFilters={isInternal}
          enableUrlSync
        />
      </div>
    </div>
  );
};
